import IPostMessage from '../IPostMessage';
import validate from '../Validate/Validate';
import IDisplay, { DisplayCapability } from './IDisplay';

/**
 * The `sos.display` API groups together methods for getting information about the device. Primarily to find out which
 * features it supports.
 *
 */
class Display implements IDisplay {
	public static MESSAGE_PREFIX: string = 'display';

	public version: string = undefined!;

	/** @internal */
	constructor(
		private messagePrefix: string,
		private postMessage: IPostMessage<any>,
	) {}

	/**
	 * The `supports()` method determines whether the device supports a queried capability.
	 *
	 * #### What are capabilities?
	 * Capabilities are features or functionalities that a device can support. We divided those capabilities into `front` and `management` capabilities.
	 * This section is about front capabilities, which include features like handling serial ports, video and stream playback, and more.
	 *
	 * On the other hand, the `management` capabilities are features that are related to the application management, such as app upgrade, app version, and more.
	 * To check `management` capabilities, refer to the `sos.management.supports()` method or [this section](https://developers.signageos.io/sdk/sos_management/#supports).
	 *
	 * :::tip
	 * If you want to check specific capabilities, refer to the sidebar for the selected management section. Every section has its capabilities written in the description,
	 * or check the **DisplayCapability** type in `supports()` methods. It has a list of all available capabilities for all platforms.
	 * :::
	 *
	 * @param capability The capability to check for support.
	 * @returns {Promise<boolean>} Resolves to `true` if the capability is supported, otherwise `false`.
	 * @since 3.1.0
	 */
	public async supports(capability: DisplayCapability): Promise<boolean> {
		validate({ capability }).string();
		const { supports } = await this.postMessage({
			type: this.getMessage('supports'),
			capability,
		});
		return supports;
	}

	/**
	 * The `getCapabilities()` method returns a list of all supported front capabilities of the device.
	 *
	 * For more information what are capabilities, see the description of the `supports()` method.
	 *
	 * @returns {Promise<DisplayCapability[]>} Resolves to an array of supported capabilities.
	 * @since 8.5.0
	 *
	 * @example
	 * const capabilities = await sos.display.getCapabilities();
	 * console.log('Supported display capabilities:', capabilities.join(', '));
	 */
	public async getCapabilities(): Promise<DisplayCapability[]> {
		const { capabilities } = await this.postMessage({
			type: this.getMessage('get_capabilities'),
		});
		return capabilities;
	}

	private getMessage(name: string) {
		return this.messagePrefix + '.' + Display.MESSAGE_PREFIX + '.' + name;
	}
}

export default Display;
